/**
 * manager-pdp.js
 * Gerencia a Product Detail Page (PDP).
 * Disponível como window._pdpManager (instância atual).
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── Módulos ──────────────────────────────────────────────────────────────
 *
 *   SizeSelectorController  → popup de seleção de tamanho
 *   AddToCartController     → intercepta form submit e delega ao CartManager.
 *                             Quando o produto exige anamnese (AnamneseModal,
 *                             ver manager-anamnesis.js), o primeiro submit é
 *                             interceptado para abrir o modal; só depois de
 *                             aprovado pelo backend é que o MESMO submit do
 *                             Product Form é reexecutado via form.requestSubmit(),
 *                             reutilizando 100% do fluxo original (quantidade,
 *                             variante, action, CartManager, analytics/pixels).
 *   GalleryObserver         → ativa âncoras do menu lateral ao scrollar
 *                             (opt-in: seção deve ter data-pdp-gallery)
 *
 * O AnamneseModal em si (perguntas dinâmicas, upload, chamada ao backend) é
 * definido em manager-anamnesis.js — este arquivo só o consulta, não o
 * implementa. Ver docs/managers/manager-anamnesis.md.
 *
 * ─── Inicialização via Liquid ─────────────────────────────────────────────
 *
 *   Na section pdp-main.liquid, dentro do bloco {% javascript %}:
 *
 *     if (typeof initPdpManager === 'function') initPdpManager('{{ section.id }}');
 *
 *   A seção é selecionada por .pdpMain--{sectionId}.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <section class="pdpMain--{sectionId}" [data-pdp-gallery]>
 *
 *     <!-- Imagens da galeria (GalleryObserver — opt-in via data-pdp-gallery) -->
 *     <div id="product-image-1">...</div>
 *     <div id="product-image-2">...</div>
 *
 *     <!-- Âncoras do menu lateral -->
 *     <a href="#product-image-1" class="pdpMain__bannerMenu">...</a>
 *
 *     <!-- Um ou mais wrappers de produto (ex: produto + produto relacionado) -->
 *     <div data-product-wrapper>
 *
 *       <!-- Seletor de tamanho -->
 *       <div data-size-selector>
 *         <button data-size-toggle aria-expanded="false">Selecionar tamanho</button>
 *         <div data-size-popup hidden>
 *           <button data-size-option data-variant-id="111" data-size-label="P">P</button>
 *           <button data-size-option data-variant-id="222" data-size-label="M">M</button>
 *         </div>
 *         <span data-size-label></span>
 *       </div>
 *
 *       <!-- Formulário de produto -->
 *       <form action="/cart/add">
 *         <input type="hidden" name="id" data-size-input>
 *         <button type="button" data-add-to-cart disabled>Selecione o tamanho</button>
 *       </form>
 *
 *     </div>
 *
 *   </section>
 */

/* ─── SizeSelectorController ──────────────────────────────────────────────── */

class SizeSelectorController {
  constructor(wrapper) {
    this._wrapper  = wrapper;
    this._toggle   = wrapper.querySelector('[data-size-toggle]');
    this._popup    = wrapper.querySelector('[data-size-popup]');
    this._options  = wrapper.querySelectorAll('[data-size-option]');
    this._label    = wrapper.querySelector('[data-size-label]');
    this._input    = wrapper.querySelector('[data-size-input]');
    this._addBtn   = wrapper.querySelector('[data-add-to-cart]');
    this._isOpen   = false;

    this._onDocClick = this._onDocClick.bind(this);
    this._onKeyDown  = this._onKeyDown.bind(this);

    this._init();
  }

  _init() {
    this._toggle?.addEventListener('click', () => this._isOpen ? this._close() : this._open());
    this._options.forEach(opt => opt.addEventListener('click', () => this._select(opt)));
    document.addEventListener('click',   this._onDocClick);
    document.addEventListener('keydown', this._onKeyDown);
  }

  _onDocClick(e) {
    if (!this._wrapper.contains(e.target)) this._close();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this._close();
  }

  _open() {
    this._popup?.classList.add('is-open');
    this._popup?.removeAttribute('hidden');
    this._toggle?.setAttribute('aria-expanded', 'true');
    this._isOpen = true;
  }

  _close() {
    this._popup?.classList.remove('is-open');
    this._popup?.setAttribute('hidden', '');
    this._toggle?.setAttribute('aria-expanded', 'false');
    this._isOpen = false;
  }

  _select(option) {
    const variantId = option.dataset.variantId;
    const label     = option.dataset.sizeLabel || option.textContent.trim();

    if (this._input)  this._input.value        = variantId;
    if (this._label)  this._label.textContent   = label;

    // Marca opção ativa
    this._options.forEach(o => o.classList.toggle('is-active', o === option));

    // Habilita o botão de compra na primeira seleção
    if (this._addBtn) {
      this._addBtn.disabled = false;
      this._addBtn.type     = 'submit';
    }

    this._close();
  }

  destroy() {
    document.removeEventListener('click',   this._onDocClick);
    document.removeEventListener('keydown', this._onKeyDown);
  }
}

/* ─── AddToCartController ─────────────────────────────────────────────────── */

class AddToCartController {
  /**
   * @param {HTMLElement} wrapper
   * @param {import('./manager-anamnesis.js').AnamneseModal|null} anamneseModal
   *        Instância já criada pelo PdpManager para o [data-anamnesis-panel]
   *        associado a este wrapper (null quando o produto não exige anamnese).
   */
  constructor(wrapper, anamneseModal = null) {
    this._form          = wrapper.querySelector('form[action*="/cart/add"]');
    this._button        = wrapper.querySelector('[data-add-to-cart]');
    this._input         = wrapper.querySelector('[data-size-input]')
                           || wrapper.querySelector('[name="id"]');
    this._qtyInput      = wrapper.querySelector('[name="quantity"]');
    this._isPending      = false;
    this._anamneseModal  = anamneseModal;

    // Flag interna: true somente entre a aprovação do backend e o próximo
    // requestSubmit() disparado por nós mesmos — evita reabrir o modal nesse
    // reenvio e é resetada assim que o item entra no carrinho (ou falha).
    this._anamneseApproved   = false;
    this._anamneseProperties = null;

    if (!this._form) return;

    this._onSubmit = this._onSubmit.bind(this);
    this._form.addEventListener('submit', this._onSubmit);
  }

  async _onSubmit(e) {
    e.preventDefault();
    if (this._isPending) return;

    const variantId = this._input?.value;
    if (!variantId) return;

    // Produto exige anamnese e ainda não foi aprovada nesta tentativa de compra:
    // interceptamos aqui, sem tocar no carrinho. AnamneseModal decide sozinho
    // se abre normal, aprovado, recusado ou erro de backend — este controller
    // só volta a agir se/quando onApproved for chamado.
    if (this._anamneseModal && !this._anamneseApproved) {
      this._anamneseModal.open({
        variantId, // sempre a variante selecionada NESTE clique, nunca a do page-load
        onApproved: (properties) => {
          this._anamneseApproved   = true;
          this._anamneseProperties = properties;
          // Reexecuta o MESMO submit do Product Form original — não duplicamos
          // a lógica de quantidade/variante/action/CartManager/analytics abaixo,
          // ela roda de novo neste mesmo _onSubmit, desta vez sem o gate.
          if (typeof this._form.requestSubmit === 'function') {
            this._form.requestSubmit();
          } else {
            this._form.submit();
          }
        },
      });
      return;
    }

    const quantity   = parseInt(this._qtyInput?.value || '1', 10) || 1;
    const action     = this._button?.dataset.addAction || 'open_drawer';
    const cartUrl    = this._button?.dataset.cartUrl   || '/cart';
    const btn        = this._button;
    // Só as duas propriedades de referência da anamnese vão para o carrinho —
    // nunca as respostas completas (ver AnamneseModal/AnamneseService).
    const properties = this._anamneseProperties || {};

    if (btn) {
      btn.style.width  = `${btn.offsetWidth}px`;
      btn.style.height = `${btn.offsetHeight}px`;
    }

    this._isPending = true;
    try {
      const result = await window.CartManager?.add(variantId, { quantity, properties, button: btn, action });

      if (result?.success && action === 'open_cart') {
        window.location.href = cartUrl;
      }
    } finally {
      this._isPending = false;
      if (btn) {
        btn.style.width  = '';
        btn.style.height = '';
      }
      // Cada tentativa de compra é independente: a próxima exige uma nova
      // aprovação de anamnese, mesmo que seja o mesmo produto/variante.
      this._anamneseApproved   = false;
      this._anamneseProperties = null;
    }
  }

  destroy() {
    this._form?.removeEventListener('submit', this._onSubmit);
  }
}

/* ─── GalleryObserver ─────────────────────────────────────────────────────── */

class GalleryObserver {
  constructor(section) {
    this._observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const anchor = section.querySelector(`a.pdpMain__bannerMenu[href="#${entry.target.id}"]`);
          if (!anchor) return;
          anchor.classList.toggle('opacity-100', entry.isIntersecting);
          anchor.classList.toggle('opacity-50',  !entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    section.querySelectorAll('[id^="product-image-"]').forEach(el => {
      this._observer.observe(el);
    });
  }

  destroy() {
    this._observer.disconnect();
  }
}

/* ─── PdpManager ──────────────────────────────────────────────────────────── */

class PdpManager {
  constructor({ sectionId }) {
    this._section = document.querySelector(`.pdpMain--${sectionId}`);
    if (!this._section) return;

    // O modal de anamnese é renderizado FORA de .pdpMain--{sectionId} de
    // propósito (ver sections/pdp-main.liquid): [data-product-wrapper] é
    // md:sticky, e position:sticky sempre cria seu próprio contexto de
    // empilhamento — um [data-drawer] preso ali dentro nunca conseguiria
    // sobrepor o header. Por isso ele busca no container externo
    // (.pdp-bg--{sectionId}), que também é ancestral de .pdpMain--{sectionId}.
    this._root = document.querySelector(`.pdp-bg--${sectionId}`) || this._section;

    this._modules = [];

    // Um AnamneseModal (manager-anamnesis.js) por modal renderizado na seção —
    // só existe quando o produto tem custom.anamnese configurado e habilitado.
    // Indexado por nome do drawer para casar com [data-anamnesis-modal] abaixo.
    const anamneseModals = new Map();
    if (typeof AnamneseModal === 'function') {
      this._root.querySelectorAll('[data-anamnesis-panel]').forEach(panel => {
        const modal = new AnamneseModal(panel);
        if (modal.name) anamneseModals.set(modal.name, modal);
        this._modules.push(modal);
      });
    }

    // Um SizeSelectorController + AddToCartController por [data-product-wrapper].
    // Quando o wrapper tem data-anamnesis-modal, o AddToCartController recebe a
    // instância de AnamneseModal correspondente e passa a exigi-la antes do add.
    this._section.querySelectorAll('[data-product-wrapper]').forEach(wrapper => {
      this._modules.push(new SizeSelectorController(wrapper));
      const modalName = wrapper.dataset.anamnesisModal;
      const anamneseModal = modalName ? anamneseModals.get(modalName) || null : null;
      this._modules.push(new AddToCartController(wrapper, anamneseModal));
    });

    // GalleryObserver é opt-in via data-pdp-gallery na seção
    if (this._section.hasAttribute('data-pdp-gallery')) {
      this._modules.push(new GalleryObserver(this._section));
    }
  }

  destroy() {
    this._modules.forEach(m => m.destroy?.());
    this._modules = [];
  }
}

/* ─── Inicialização ───────────────────────────────────────────────────────── */

function initPdpManager(sectionId) {
  if (window._pdpManager) window._pdpManager.destroy();
  window._pdpManager = new PdpManager({ sectionId });
}

window.initPdpManager = initPdpManager;

// Auto-inicialização: escaneia o DOM por seções PDP declaradas via data attribute.
// Resolve o problema de timing onde {% javascript %} executa antes deste script.
document.querySelectorAll('[data-pdp-section-id]').forEach(el => {
  initPdpManager(el.dataset.pdpSectionId);
});